import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState, selectLanguageInCommonState } from 'app/app-state.index';
import { Roulette } from 'app/public/model/roulette.model';
import { WebRouletteService } from 'app/public/services/web-roulette.service';
import { Language } from 'app/shared/model/enums';
import { LAST_CONNECTED_LANGUAGE } from 'app/shared/model/local-storage-keys';
import { Subscriber, Subscription } from 'rxjs';

@Component({
  selector: 'app-roulette-design-settings',
  templateUrl: './roulette-design-settings.component.html',
  styleUrls: ['./roulette-design-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RouletteDesignSettingsComponent implements OnInit {

  @Input()
  roulette: Roulette;

  @Input() form: NgForm;

  spinnersByCategory: Map<number, any> = new Map<number, any>();

  spinnersCategories: { Id: number, Description: string }[];

  _selectedCategory: number;
  get selectedCategory() {
    return this._selectedCategory;
  }
  set selectedCategory(value) {
    if (value !== this._selectedCategory) {
      this._selectedCategory = value;
      // switching category exits custom-spinner mode and shows the gallery
      if (this.roulette?.Design) {
        this.roulette.Design.IsCustomSpinner = false;
        const fallback = this._previousSpinner || this.getFirstAvailableSpinner();
        if (fallback) {
          this.roulette.Design.Spinner = fallback;
        }
      }
      this.loadSpinners();
    }
  }

  subscriptions = new Subscription();
  language: Language;

  get currentSpinners() {
    return this.spinnersByCategory.get(this._selectedCategory);
  }
  get selectedSpinnerId() {
    return this.roulette?.Design.Spinner?.Id;
  }
  get isCustomSpinner(): boolean {
    return this.roulette?.Design.IsCustomSpinner || false;
  }

  constructor(private rouletteService: WebRouletteService, private cdr: ChangeDetectorRef, private zone: NgZone, store: Store<AppState>) {

    this.subscriptions.add(store.select(selectLanguageInCommonState).subscribe(language => {
      this.language = language;
    }));

  }

  ngOnInit(): void {
    this.loadSpinnersCategories();
    this.loadSpinnerButtons();
  }

  loadSpinnersCategories() {
    this.rouletteService.getSpinnersCategories(this.language).subscribe(res => {
      this.spinnersCategories = res as [];
      this.selectedCategory = 1;

    })
  }

  loadSpinners() {
    if (this._selectedCategory > 0) {
      this.rouletteService.getSpinnersByCategory(this.selectedCategory, this.language).subscribe(
        spinners => {
          this.spinnersByCategory.set(this._selectedCategory, spinners);
          this.cdr.markForCheck();
        }
      );
    }
  }

  loadSpinnerButtons() {
    this.rouletteService.getSpinnerButtonUrls().subscribe(urls => {
      this.spinnerButtonUrls = urls || [];
      this.cdr.markForCheck();
    });
  }

  getFirstAvailableSpinner() {
    // iterate all loaded categories to find the first available spinner
    for (const spinners of Array.from(this.spinnersByCategory.values())) {
      if (spinners && spinners.length > 0) {
        return spinners[0];
      }
    }
    return null;
  }

  _previousSpinner: any = null;
  bgUploadError: string = null;
  spinnerButtonUrls: string[] = [];
  private readonly MAX_BG_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB כדי להישאר בטוח גם אחרי base64 + מעטפת JSON

  toggleCustomSpinner() {
    if (this.isCustomSpinner) {
      // collapse — restore previous spinner, or fall back to first available
      this.roulette.Design.IsCustomSpinner = false;
      const fallback = this._previousSpinner || this.getFirstAvailableSpinner();
      if (fallback) {
        this.roulette.Design.Spinner = fallback;
      }
      this.form.form.markAsDirty();
    } else {
      this.activateCustomSpinner();
    }
  }

  selectSpinner(spinner) {
    this.roulette.Design.IsCustomSpinner = false;
    this.roulette.Design.Spinner = spinner;
    this.form.form.markAsDirty();
  }

  activateCustomSpinner() {
    // save current spinner so we can restore it if user cancels
    if (!this.isCustomSpinner && this.roulette.Design.Spinner?.Id > 0) {
      this._previousSpinner = this.roulette.Design.Spinner;
    }
    this.roulette.Design.IsCustomSpinner = true;
    this.roulette.Design.CustomDarkColor = this.roulette.Design.CustomDarkColor || '#000000';
    this.roulette.Design.CustomLightColor = this.roulette.Design.CustomLightColor || '#ffffff';
    this.roulette.Design.Spinner = {
      Id: 0,
      SpinnerName: 'Custom',
      Description: '',
      ThumbUrl: '',
      PlateUrl: '',
      ButtonUrl: this.roulette.Design.CustomButtonUrl
        || 'https://stwstorageaccount.blob.core.windows.net/spinners/Button/2.png',
      BackgroundImageUrl: this.roulette.Design.CustomBackgroundImageUrl
        || 'https://stwstorageaccount.blob.core.windows.net/spinners/BackgroundImage/white-bg.png',
      LightColor: this.roulette.Design.CustomLightColor,
      DarkColor: this.roulette.Design.CustomDarkColor,
    };
    this.form.form.markAsDirty();
  }

  onCustomColorChange() {
    if (this.roulette.Design.IsCustomSpinner) {
      this.roulette.Design.Spinner.DarkColor = this.roulette.Design.CustomDarkColor;
      this.roulette.Design.Spinner.LightColor = this.roulette.Design.CustomLightColor;
    }
    this.form.form.markAsDirty();
  }

  onCustomBackgroundChange(base64: string) {
    this.roulette.Design.newCustomBackgroundImageAsBase64 = base64;
    this.form.form.markAsDirty();
  }

  onBgFileSelected(event: Event) {
    this.bgUploadError = null;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > this.MAX_BG_FILE_SIZE_BYTES) {
      this.bgUploadError = 'ROULETTE.BG_FILE_TOO_LARGE';
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.zone.run(() => {
        const base64 = reader.result as string;
        this.roulette.Design.CustomBackgroundImageUrl = base64;
        this.roulette.Design.newCustomBackgroundImageAsBase64 = base64;
        this.form.form.markAsDirty();
        this.cdr.detectChanges();
      });
    };
  }

  previewCustomBackground() {
    if (this.roulette.Design.CustomBackgroundImageUrl && this.roulette.Design.Spinner) {
      this.roulette.Design.Spinner = { ...this.roulette.Design.Spinner, BackgroundImageUrl: this.roulette.Design.CustomBackgroundImageUrl };
    }
  }

  removeCustomBackground() {
    this.bgUploadError = null;
    this.roulette.Design.CustomBackgroundImageUrl = null;
    this.roulette.Design.newCustomBackgroundImageAsBase64 = null;
    if (this.roulette.Design.Spinner) {
      this.roulette.Design.Spinner = { ...this.roulette.Design.Spinner, BackgroundImageUrl: 'https://stwstorageaccount.blob.core.windows.net/spinners/BackgroundImage/white-bg.png' };
    }
    this.form.form.markAsDirty();
  }

  selectButton(buttonUrl: string) {
    this.roulette.Design.CustomButtonUrl = buttonUrl;
    if (this.roulette.Design.Spinner) {
      this.roulette.Design.Spinner = { ...this.roulette.Design.Spinner, ButtonUrl: buttonUrl };
    }
    this.form.form.markAsDirty();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
